import React, { useCallback, useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  ArrowUpRight, BarChart3, CheckCircle2, ChevronRight,
  Clock3, Globe2, Lightbulb, Link2, RefreshCw, ShieldCheck,
  SkipForward, Sparkles, Target, Users,
} from "lucide-react";
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaRedditAlien, FaTiktok, FaYoutube } from "react-icons/fa";
import "../onboarding.css";

const VALUE_LABELS = {
  "50_plus": "50+",
  "58_plus": "58+",
  "65_plus": "65+",
  "79_plus": "79+",
  "85_plus": "85+",
  not_sure: "Not sure yet",
  within_7_days: "Within 7 days",
  "1_2_weeks": "1–2 weeks",
  "2_4_weeks": "2–4 weeks",
  more_than_1_month: "More than 1 month",
  not_booked: "Not booked",
  study_abroad: "Study abroad",
  permanent_residency: "Permanent Residency",
  work_professional: "Work / Professional",
  visa_immigration: "Visa / Immigration",
  personal_academic: "Personal / Academic",
  google: "Google Search",
  youtube: "YouTube",
  instagram: "Instagram",
  tiktok: "TikTok",
  reddit: "Reddit",
  facebook: "Facebook",
  pte_tutor: "PTE tutor / Coaching centre",
  friend_family: "Friend or family",
  study_abroad_consultant: "Study-abroad consultant",
  immigration_consultant: "Immigration consultant",
  other: "Other",
  x_twitter: "X / Twitter",
  linkedin: "LinkedIn",
  pinterest: "Pinterest",
  snapchat: "Snapchat",
  threads: "Threads",
  whatsapp: "WhatsApp",
  meta: "Meta (platform unspecified)",
  search: "Search",
  social: "Social media",
  word_of_mouth: "Word of mouth",
  tutors_consultants: "Tutors & consultants",
};

const QUESTION_BREAKDOWNS = [
  { key: "target_score", title: "Target score", subtitle: "Score learners are aiming for" },
  { key: "exam_timeline", title: "Exam timeline", subtitle: "How soon learners plan to test" },
  { key: "pte_goal", title: "PTE goal", subtitle: "Primary reason for preparation" },
  { key: "acquisition_source", title: "Self-reported discovery", subtitle: "How learners say they found PrepNexis" },
];

const ATTRIBUTION_BREAKDOWNS = [
  { key: "first_touch_attribution.utm_source", title: "UTM source" },
  { key: "first_touch_attribution.utm_medium", title: "UTM medium" },
  { key: "first_touch_attribution.utm_campaign", title: "UTM campaign" },
];

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatLabel = (value) => VALUE_LABELS[value] || String(value || "Unknown").replace(/_/g, " ");
const formatShare = (count, total) => total ? `${Math.round((count / total) * 1000) / 10}%` : "0%";

const STATUS_COLORS = { completed: "var(--appearance-chart, #17806b)", skipped: "#c9a15c", pending: "#a7b4aa" };
const PLATFORM_ICONS = { instagram: FaInstagram, youtube: FaYoutube, facebook: FaFacebookF, tiktok: FaTiktok, reddit: FaRedditAlien, linkedin: FaLinkedinIn };
const jumpLinks = [
  { id: "onboarding-overview", label: "Overview", description: "Signups & progress", icon: BarChart3 },
  { id: "onboarding-profile", label: "Learner profile", description: "Scores, timing & goals", icon: Users },
  { id: "onboarding-acquisition", label: "Acquisition", description: "Sources & campaigns", icon: Globe2 },
  { id: "onboarding-insights", label: "Insights", description: "Findings & next steps", icon: Lightbulb },
];

function SectionHeading({ eyebrow, title, description, icon: Icon }) {
  return (
    <div className="onb-section-heading">
      <div>
        <p className="admin-eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p className="onb-section-description">{description}</p>
      </div>
      {Icon && <span className="onb-section-icon"><Icon size={20} strokeWidth={1.6} /></span>}
    </div>
  );
}

function BreakdownCard({ title, subtitle, rows = [], responseTotal, tone = "green" }) {
  const total = responseTotal ?? rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
  const leading = rows.reduce((best, row) => Number(row.count || 0) > Number(best?.count || 0) ? row : best, null);
  return (
    <article className={`admin-card onb-breakdown onb-tone-${tone}`} aria-label={title}>
      <div className="admin-card-heading">
        <div><h3>{title}</h3>{subtitle && <p>{subtitle}</p>}</div>
        <span className="admin-count-badge">{formatNumber(total)} responses</span>
      </div>
      {rows.length === 0 ? <div className="onb-empty"><BarChart3 size={23} /><p>No responses yet.</p></div> : (
        <div className="onb-breakdown-rows">
          {rows.map((row) => {
            const share = total ? (Number(row.count || 0) / total) * 100 : 0;
            return (
              <div key={row.value} className={`onb-distribution-row${row === leading ? " is-leading" : ""}`}>
                {row === leading && <span className="onb-leading-label">Most selected</span>}
                <div className="onb-distribution-label"><span title={formatLabel(row.value)}>{formatLabel(row.value)}</span><strong>{formatNumber(row.count)} <span>· {formatShare(row.count, total)}</span></strong></div>
                <div className="onb-progress-track" aria-hidden="true"><span style={{ width: `${Math.min(100, Math.max(0, share))}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
      <p className="onb-card-note">Share of recorded answers{rows.length === 20 ? " · top 20 shown" : ""}.</p>
    </article>
  );
}

function CompletionMix({ summary }) {
  const total = Number(summary.initialized || 0);
  const completed = total ? (Number(summary.completed || 0) / total) * 100 : 0;
  const skipped = total ? (Number(summary.skipped || 0) / total) * 100 : 0;
  const entries = [
    { key: "completed", label: "Completed", value: summary.completed },
    { key: "skipped", label: "Skipped", value: summary.skipped },
    { key: "pending", label: "Pending", value: summary.pending },
  ];
  return (
    <article className="admin-card onb-completion-card">
      <div className="admin-card-heading"><div><h3>Onboarding progress</h3><p>Every new account, one clear picture</p></div><CheckCircle2 size={19} className="admin-muted-icon" /></div>
      <div className="admin-donut onb-donut" role="img" aria-label={`${summary.completionRate || 0}% completed; ${formatNumber(summary.completed)} completed, ${formatNumber(summary.skipped)} skipped, ${formatNumber(summary.pending)} pending.`} style={{ background: total ? `conic-gradient(${STATUS_COLORS.completed} 0% ${completed}%, ${STATUS_COLORS.skipped} ${completed}% ${completed + skipped}%, ${STATUS_COLORS.pending} ${completed + skipped}% 100%)` : "#edf1ef" }}>
        <div><strong>{summary.completionRate || 0}%</strong><span>completion rate</span></div>
      </div>
      <ul className="admin-subscription-legend onb-status-legend">
        {entries.map((entry) => <li key={entry.key}><span className="admin-legend-dot" style={{ background: STATUS_COLORS[entry.key] }} /><span className="onb-status-label">{entry.label}</span><strong>{formatNumber(entry.value)}</strong><span>{formatShare(entry.value || 0, total)}</span></li>)}
      </ul>
      <p className="onb-card-note">Pending learners may return later.</p>
    </article>
  );
}

function CohortChart({ monthly = [] }) {
  const monthLabel = (value) => {
    const date = new Date(`${value}-01T00:00:00Z`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
  };
  return (
    <article className="admin-card onb-cohort-card">
      <div className="admin-card-heading"><div><h3>New account cohorts</h3><p>Signups and their current onboarding status</p></div><span className="onb-period-badge">Last 12 months</span></div>
      <div className="onb-chart-legend">{[["Initialized", "#8caaa0"], ["Completed", STATUS_COLORS.completed], ["Skipped", STATUS_COLORS.skipped]].map(([label, color]) => <span key={label}><i style={{ background: color }} />{label}</span>)}</div>
      {monthly.length ? <div className="onb-cohort-chart" role="img" aria-label="Monthly initialized, completed, and skipped onboarding accounts">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={monthly} margin={{ top: 10, right: 10, left: -18, bottom: 2 }} barGap={3}>
            <CartesianGrid stroke="var(--appearance-card-border, #edf0ee)" strokeDasharray="3 4" vertical={false} />
            <XAxis dataKey="month" tickFormatter={monthLabel} axisLine={false} tickLine={false} tickMargin={12} tick={{ fill: "var(--appearance-card-muted, #526259)", fontSize: 13 }} minTickGap={26} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: "var(--appearance-card-muted, #526259)", fontSize: 13 }} />
            <Tooltip cursor={{ fill: "var(--appearance-card-soft, #f2f5f3)" }} contentStyle={{ borderRadius: 9, border: "1px solid var(--appearance-card-border, #e4e9e6)", background: "var(--appearance-surface, white)", color: "var(--appearance-card-text, #111827)", fontSize: 15 }} />
            <Bar dataKey="initialized" fill="#8caaa0" name="Initialized" radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="completed" fill={STATUS_COLORS.completed} name="Completed" radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
            <Bar dataKey="skipped" fill={STATUS_COLORS.skipped} name="Skipped" radius={[3, 3, 0, 0]} maxBarSize={18} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div> : <div className="onb-empty onb-chart-empty"><BarChart3 size={28} /><p>No dated accounts in the last 12 months.</p></div>}
      <p className="onb-card-note">Calendar months in UTC. Completion and skip reflect current status, not the month of action.</p>
    </article>
  );
}

function SourceAnalysis({ data }) {
  const analysis = data?.sourceAnalysis || {};
  const initialized = data?.summary?.initialized || 0;
  const reportedCoverage = analysis.reportedCoverage ?? data?.responseTotals?.acquisition_source ?? 0;
  const platforms = analysis.socialPlatforms || [];
  const channels = analysis.reportedChannels || [];
  const maxPlatformCount = Math.max(1, ...platforms.flatMap((row) => [Number(row.tracked || 0), Number(row.reported || 0)]));
  return (
    <>
      <section className="onb-source-summary" aria-label="Signup-source coverage">
        <div><span className="onb-source-icon"><Globe2 size={18} /></span><div><p>Tracked social signups</p><strong>{formatNumber(analysis.trackedSocial)}</strong><span>{formatShare(analysis.trackedSocial || 0, initialized)} of new accounts</span></div></div>
        <div><span className="onb-source-icon onb-source-survey"><Users size={18} /></span><div><p>Social survey mentions</p><strong>{formatNumber(analysis.reportedSocial)}</strong><span>{formatShare(analysis.reportedSocial || 0, reportedCoverage)} of answered discovery questions</span></div></div>
        <div><span className="onb-source-icon onb-source-coverage"><Link2 size={18} /></span><div><p>Tracked-source coverage</p><strong>{formatNumber(analysis.trackedCoverage)} <small>/ {formatNumber(initialized)}</small></strong><span>Accounts with a UTM source or referrer</span></div></div>
      </section>
      <div className="onb-sources-grid">
        <article className="admin-card onb-platforms-card">
          <div className="admin-card-heading"><div><h3>Which social platforms?</h3><p>Tracked first touch alongside self-reported discovery</p></div><Globe2 size={18} className="admin-muted-icon" /></div>
          {platforms.length ? <div className="onb-platforms-table">
            <div className="onb-platform-columns"><span>Platform</span><span>Tracked</span><span>Survey</span></div>
            {platforms.map((row) => {
              const Icon = PLATFORM_ICONS[row.value] || Globe2;
              return <div key={row.value} className="onb-platform-row">
                <span className="onb-platform-name"><span className="onb-platform-icon"><Icon size={15} /></span>{formatLabel(row.value)}</span>
                <div className="onb-platform-count"><strong>{formatNumber(row.tracked)}</strong><div className="onb-progress-track" aria-hidden="true"><span style={{ width: `${(Number(row.tracked || 0) / maxPlatformCount) * 100}%` }} /></div></div>
                <div className="onb-platform-count onb-survey-count"><strong>{formatNumber(row.reported)}</strong><div className="onb-progress-track" aria-hidden="true"><span style={{ width: `${(Number(row.reported || 0) / maxPlatformCount) * 100}%` }} /></div></div>
              </div>;
            })}
          </div> : <div className="onb-empty"><Globe2 size={24} /><p>No social platform has been identified yet.</p></div>}
          <p className="onb-card-note">Counts can include the same person. Do not add tracked and survey counts together.</p>
        </article>
        <BreakdownCard title="Discovery channels from the survey" subtitle={`Share of ${formatNumber(reportedCoverage)} accounts that answered the discovery question.`} rows={channels} responseTotal={reportedCoverage} tone="sage" />
      </div>
      <p className="onb-data-note"><ShieldCheck size={14} />Signup sources, not all website visits. First touch uses a social UTM source, or a social referrer when no source was captured.</p>
    </>
  );
}

function DataSignals({ data }) {
  const summary = data?.summary || {};
  const top = (field) => data?.breakdowns?.[field]?.[0];
  const responseTotal = (field) => data?.responseTotals?.[field] ?? data?.breakdowns?.[field]?.reduce((sum, row) => sum + Number(row.count || 0), 0) ?? 0;
  const sourceCoverage = data?.sourceAnalysis?.trackedCoverage ?? responseTotal("first_touch_attribution.utm_source");
  if (!summary.initialized) return <div className="onb-empty"><Lightbulb size={24} /><p>Insights will appear after the first new account begins onboarding.</p></div>;
  const signals = [{
    title: "Onboarding progress", icon: CheckCircle2,
    evidence: `${formatNumber(summary.completed)} of ${formatNumber(summary.initialized)} accounts answered all four questions (${summary.completionRate}%). ${formatNumber(summary.pending)} are still pending.`,
    action: "Review skip and pending counts before drawing conclusions about form friction.",
  }];
  const leadingTimeline = top("exam_timeline");
  if (leadingTimeline) signals.push({ title: "Most common exam timing", icon: Clock3,
    evidence: `${formatLabel(leadingTimeline.value)} is the largest group with ${formatNumber(leadingTimeline.count)} of ${formatNumber(responseTotal("exam_timeline"))} recorded answers.`,
    action: "Check whether the practice entry point serves this timeframe well.",
  });
  const leadingGoal = top("pte_goal");
  if (leadingGoal) signals.push({ title: "Leading preparation goal", icon: Target,
    evidence: `${formatLabel(leadingGoal.value)} leads with ${formatNumber(leadingGoal.count)} of ${formatNumber(responseTotal("pte_goal"))} recorded answers.`,
    action: "Compare the first practice experience with this learner goal.",
  });
  signals.push({ title: "Marketing attribution coverage", icon: Link2,
    evidence: `${formatNumber(sourceCoverage)} of ${formatNumber(summary.initialized)} new accounts have a captured UTM source or referrer.`,
    action: "Use self-reported discovery alongside UTM data; neither alone identifies every source.",
  });
  const leadingSocial = data?.sourceAnalysis?.socialPlatforms?.[0];
  if (leadingSocial) signals.push({ title: "Leading social platform", icon: Globe2,
    evidence: `${formatLabel(leadingSocial.value)} appears in ${formatNumber(leadingSocial.tracked)} tracked first touches and ${formatNumber(leadingSocial.reported)} survey answers. These may overlap.`,
    action: "Compare this platform’s campaigns with the other social sources before shifting effort.",
  });
  return <div className="onb-signals-grid">{signals.map(({ title, evidence, action, icon: Icon }) => <article key={title} className="admin-card onb-signal-card">
    <div className="onb-signal-title"><span><Icon size={17} /></span><h3>{title}</h3></div>
    <p className="onb-signal-evidence"><span className="onb-detail-label">What the data shows</span>{evidence}</p><div className="onb-signal-action"><ArrowUpRight size={18} /><div><span className="onb-detail-label">Suggested next step</span>{action}</div></div>
  </article>)}</div>;
}

function SkipSteps({ rows = [] }) {
  const total = rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
  const labels = ["Target score", "Exam timeline", "PTE goal", "Discovery source"];
  return <article className="admin-card onb-skip-card">
    <div className="admin-card-heading"><div><h3>Where learners skip</h3><p>Question reached when “Skip for now” was selected</p></div><SkipForward size={18} className="admin-muted-icon" /></div>
    <ol className="onb-skip-list">{labels.map((label, index) => {
      const count = rows.find((row) => row.step === index + 1)?.count || 0;
      return <li key={label}><span className="onb-step-number">{index + 1}</span><div><span>{label}</span><div className="onb-progress-track" aria-hidden="true"><span style={{ width: `${total ? (count / total) * 100 : 0}%` }} /></div></div><strong>{formatNumber(count)}</strong></li>;
    })}</ol>
    <p className="onb-card-note">These counts do not measure abandonment.</p>
  </article>;
}

export default function OnboardingAnalytics() {
  const [activeSection, setActiveSection] = useState("onboarding-overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackError, setFeedbackError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch("/api/admin/onboarding-analytics", { credentials: "same-origin", cache: "no-store" });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Unable to load onboarding analytics.");
      if (["initialized", "completed", "skipped", "pending"].some((key) => !Number.isFinite(result.summary?.[key]))) throw new Error("Onboarding totals are unavailable. Please refresh to try again.");
      setData(result);
      setFeedback(null);
      setFeedbackError("");
    } catch (loadError) {
      setData(null);
      setError(loadError.message || "Unable to load onboarding analytics.");
    } finally { setLoading(false); }
  }, []);
  const generateFeedback = async () => {
    setFeedbackLoading(true);
    setFeedbackError("");
    try {
      const response = await fetch("/api/admin/onboarding-analytics/ai-feedback", {
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Unable to generate AI feedback.");
      setFeedback(result);
    } catch (requestError) { setFeedbackError(requestError.message || "Unable to generate AI feedback. Please retry."); }
    finally { setFeedbackLoading(false); }
  };
  useEffect(() => { load(); }, [load]);
  const handleTabKey = (event, index) => {
    let nextIndex;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % jumpLinks.length;
    else if (event.key === "ArrowLeft") nextIndex = (index + jumpLinks.length - 1) % jumpLinks.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = jumpLinks.length - 1;
    else return;
    event.preventDefault();
    setActiveSection(jumpLinks[nextIndex].id);
    event.currentTarget.parentElement.children[nextIndex].focus();
  };
  const summary = data?.summary || {};
  const summaryCards = [
    { key: "accounts", label: "Onboarding accounts", value: summary.initialized, helper: "New accounts initialized", icon: Users },
    { key: "completed", label: "Completed", value: summary.completed, helper: `${summary.completionRate || 0}% answered all questions`, icon: CheckCircle2 },
    { key: "skipped", label: "Skipped", value: summary.skipped, helper: "Used Skip for now", icon: SkipForward },
    { key: "pending", label: "Pending", value: summary.pending, helper: `${summary.resolvedRate || 0}% completed or skipped`, icon: Clock3 },
  ];
  return (
    <section className="onb-page" aria-labelledby="onboarding-analytics-heading" aria-busy={loading}>
      <div className="admin-page-heading">
        <div><p className="admin-eyebrow">UNDERSTAND EVERY NEW START</p><h1 id="onboarding-analytics-heading">Onboarding analytics<span className="admin-title-dot">.</span></h1><p className="admin-page-description">See who’s joining, what they need, and how they find you.</p></div>
        <button type="button" onClick={load} disabled={loading || feedbackLoading} className="admin-button admin-button-secondary"><RefreshCw size={15} className={loading ? "animate-spin" : ""} />{loading ? "Refreshing…" : "Refresh"}</button>
      </div>
      <div className="onb-section-tabs" role="tablist" aria-label="Onboarding analytics sections">{jumpLinks.map(({ id, label, description, icon: Icon }, index) => <button key={id} type="button" role="tab" id={`${id}-tab`} aria-controls={id} aria-selected={activeSection === id} tabIndex={activeSection === id ? 0 : -1} onClick={() => setActiveSection(id)} onKeyDown={(event) => handleTabKey(event, index)}><Icon size={21} /><span><strong>{label}</strong><small>{description}</small></span></button>)}</div>
      {error && <p role="alert" className="admin-alert">{error}</p>}
      {loading ? <div className="onb-loading" aria-label="Loading onboarding analytics"><div className="admin-stats-grid">{[1, 2, 3, 4].map((item) => <div key={item} className="admin-card onb-loading-card"><span className="admin-skeleton" /><span className="admin-skeleton" /><span className="admin-skeleton" /></div>)}</div><div className="admin-card onb-loading-chart"><span className="admin-skeleton" /></div></div> : data ? <>
        <section id="onboarding-overview" role="tabpanel" aria-labelledby="onboarding-overview-tab" tabIndex={0} hidden={activeSection !== "onboarding-overview"} className="onb-section">
          <SectionHeading eyebrow="ALL TIME · CURRENT ACCOUNT STATUS" title="Onboarding performance" description="Start here for signup volume and completion. Explore the other tabs for details." icon={BarChart3} />
          <div className="admin-stats-grid">{summaryCards.map(({ key, label, value, helper, icon: Icon }) => <article key={key} className={`admin-card admin-stat-card onb-stat-${key}`}>
            <div className="admin-stat-heading"><span className="admin-stat-icon"><Icon size={18} strokeWidth={1.7} /></span><p>{label}</p></div><p className="admin-stat-value">{formatNumber(value)}</p><p className="onb-stat-helper">{helper}</p>
          </article>)}</div>
          <div className="onb-overview-grid"><CohortChart monthly={data.monthly} /><CompletionMix summary={summary} /></div>
        </section>
        <section id="onboarding-profile" role="tabpanel" aria-labelledby="onboarding-profile-tab" tabIndex={0} hidden={activeSection !== "onboarding-profile"} className="onb-section">
          <SectionHeading eyebrow="LEARNER NEEDS" title="Meet your newest learners" description="Find their target score, exam timing, and preparation goal. Each card highlights the most selected answer." icon={Target} />
          <div className="onb-profile-grid">{QUESTION_BREAKDOWNS.filter(({ key }) => key !== "acquisition_source").map(({ key, ...item }, index) => <BreakdownCard key={key} {...item} rows={data?.breakdowns?.[key]} responseTotal={data?.responseTotals?.[key]} tone={["green", "sage", "blue"][index]} />)}</div>
          <div className="onb-group-heading"><h3>Patterns & skipped questions</h3><p>Compare score and timing together, then see where learners chose to skip.</p></div>
          <div className="onb-profile-bottom">
            <article className="admin-card onb-combinations-card"><div className="admin-card-heading"><div><h3>Common score and exam timing combinations</h3><p>Top combinations among learners who completed onboarding</p></div><Target size={18} className="admin-muted-icon" /></div>
              {data.scoreTimeline?.length ? <ol className="onb-combinations">{data.scoreTimeline.map((row, index) => <li key={`${row.target_score}-${row.exam_timeline}`}><span className="onb-combination-rank">{String(index + 1).padStart(2, "0")}</span><div><strong>{formatLabel(row.target_score)}</strong><span>{formatLabel(row.exam_timeline)}</span></div><span className="onb-combination-count">{formatNumber(row.count)}<small>learners</small></span></li>)}</ol> : <div className="onb-empty"><Target size={24} /><p>No completed responses yet.</p></div>}
            </article><SkipSteps rows={data.skipSteps} />
          </div>
        </section>
        <section id="onboarding-acquisition" role="tabpanel" aria-labelledby="onboarding-acquisition-tab" tabIndex={0} hidden={activeSection !== "onboarding-acquisition"} className="onb-section">
          <SectionHeading eyebrow="SIGNUP SOURCES" title="Where new accounts come from" description="Compare captured signup sources with learners’ survey answers, then explore campaign details below." icon={Globe2} />
          <SourceAnalysis data={data} />
          <div className="onb-group-heading"><h3>What learners told us</h3><p>Specific sources selected in the discovery question.</p></div>
          <BreakdownCard title="Self-reported discovery" subtitle="How learners say they found PrepNexis" rows={data?.breakdowns?.acquisition_source} responseTotal={data?.responseTotals?.acquisition_source} tone="amber" />
          <div className="onb-group-heading onb-attribution-heading"><Link2 size={22} /><div><h3>First-touch marketing attribution</h3><p>Campaign source, medium, and name captured before signup.</p></div></div>
          <div className="onb-attribution-grid">{ATTRIBUTION_BREAKDOWNS.map(({ key, ...item }) => <BreakdownCard key={key} {...item} rows={data?.breakdowns?.[key]} responseTotal={data?.responseTotals?.[key]} />)}</div>
        </section>
        <section id="onboarding-insights" role="tabpanel" aria-labelledby="onboarding-insights-tab" tabIndex={0} hidden={activeSection !== "onboarding-insights"} className="onb-section">
          <SectionHeading eyebrow="FINDINGS & NEXT STEPS" title="Data signals" description="Read the finding first, then the suggested action. These summaries update whenever you refresh." icon={Lightbulb} />
          <DataSignals data={data} />
          <article className="admin-card onb-ai-card" aria-busy={feedbackLoading}>
            <div className="onb-ai-heading"><span className="onb-ai-icon"><Sparkles size={22} /></span><div><span className="admin-eyebrow">A FRESH PERSPECTIVE</span><h3>AI feedback</h3><p>Recommendations from aggregate counts. No names, emails, or individual responses are sent.</p></div>
              <button type="button" onClick={generateFeedback} disabled={feedbackLoading || !data.aiAvailable || !summary.initialized} className="admin-button admin-button-primary"><Sparkles size={15} />{feedbackLoading ? "Analyzing…" : feedback ? "Regenerate feedback" : "Generate AI feedback"}</button>
            </div>
            {!data.aiAvailable && <p className="onb-ai-notice">AI feedback is unavailable for this workspace. Data signals remain available.</p>}
            {data.aiAvailable && !summary.initialized && <p className="onb-ai-notice">Recommendations will be available after the first account begins onboarding.</p>}
            {feedbackError && <p role="alert" className="admin-alert onb-feedback-error">{feedbackError}</p>}
            {feedbackLoading && <p role="status" className="onb-ai-loading"><RefreshCw size={14} className="animate-spin" />Reviewing your onboarding patterns…</p>}
            {feedback && <div className="onb-feedback-results"><p className="onb-feedback-overview">{feedback.overview}</p><div className="onb-feedback-grid">{(feedback.insights || []).map((insight, index) => <article key={`${insight.title}-${index}`} className="onb-feedback-insight"><div><h4>{insight.title}</h4><span className={`onb-priority priority-${insight.priority}`}>{insight.priority}</span></div><p><strong>Evidence</strong>{insight.evidence}</p><p><strong>Suggested action</strong>{insight.recommendation}</p></article>)}</div><p className="onb-card-note">Generated by {feedback.model}. Review recommendations before making product decisions.</p></div>}
            {!feedback && !feedbackLoading && data.aiAvailable && summary.initialized > 0 && <div className="onb-ai-placeholder"><Lightbulb size={18} /><span>Ready when you are. Generate feedback to explore your next steps.</span><ChevronRight size={16} /></div>}
          </article>
        </section>
      </> : <div className="admin-card onb-load-error"><BarChart3 size={30} /><h2>Let’s try that again</h2><p>Analytics could not be loaded. Use Refresh to try again.</p></div>}
    </section>
  );
}
